import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronDown, LogOut, RefreshCw, FileText, Inbox } from "lucide-react";
import { ADMIN_API, useAdminAuth } from "./AdminAuth";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const Row = ({ label, value, href }) => (
  <div className="flex flex-col gap-0.5 border-b border-line/60 py-2.5 sm:flex-row sm:gap-6">
    <span className="w-40 shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] text-ash">{label}</span>
    {href ? (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-sm text-brand hover:text-brand-hover break-all">
        {value}
      </a>
    ) : (
      <span className="text-sm text-cream whitespace-pre-wrap break-words">{value || "—"}</span>
    )}
  </div>
);

const AdminDashboard = () => {
  const { setAdmin } = useAdminAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [q, m] = await Promise.all([
        axios.get(`${ADMIN_API}/admin/quotes`, { withCredentials: true }),
        axios.get(`${ADMIN_API}/admin/messages`, { withCredentials: true }),
      ]);
      setQuotes(q.data);
      setMessages(m.data);
    } catch (e) {
      if (e.response?.status === 401) {
        setAdmin(false);
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    await axios.post(`${ADMIN_API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setAdmin(false);
    navigate("/admin/login");
  };

  const items = tab === "quotes" ? quotes : messages;

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-line bg-ink/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-4">
            <span className="inline-flex rounded-lg bg-white px-3 py-1.5">
              <img src="/assets/logo-light.png" alt="OMNIBÂT" className="h-8 w-auto" />
            </span>
            <span className="hidden sm:block font-mono text-[11px] uppercase tracking-[0.25em] text-brand">Back-office</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              data-testid="admin-refresh-button"
              className="flex items-center gap-2 border border-line px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-fog transition-colors hover:border-brand hover:text-brand"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Actualiser
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 border border-line px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-fog transition-colors hover:border-red-500 hover:text-red-400"
            >
              <LogOut className="h-3.5 w-3.5" /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="admin-tab-quotes"
            onClick={() => setTab("quotes")}
            className={`flex items-center gap-2 border px-5 py-3 font-display text-base font-bold uppercase tracking-[0.1em] transition-colors ${
              tab === "quotes" ? "border-brand bg-brand text-ink" : "border-line text-fog hover:border-brand hover:text-brand"
            }`}
          >
            <FileText className="h-4 w-4" /> Demandes de devis ({quotes.length})
          </button>
          <button
            data-testid="admin-tab-messages"
            onClick={() => setTab("messages")}
            className={`flex items-center gap-2 border px-5 py-3 font-display text-base font-bold uppercase tracking-[0.1em] transition-colors ${
              tab === "messages" ? "border-brand bg-brand text-ink" : "border-line text-fog hover:border-brand hover:text-brand"
            }`}
          >
            <Inbox className="h-4 w-4" /> Messages ({messages.length})
          </button>
        </div>

        {loading ? (
          <div className="mt-16 flex justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand border-t-transparent" />
          </div>
        ) : items.length === 0 ? (
          <p data-testid="admin-empty-state" className="mt-16 text-center font-mono text-xs uppercase tracking-[0.2em] text-ash">
            Aucun élément pour le moment
          </p>
        ) : (
          <div className="mt-8 space-y-3" data-testid={`admin-${tab}-list`}>
            {items.map((item) => {
              const open = openId === item.id;
              const title = tab === "quotes" ? item.name : `${item.name} — ${item.subject}`;
              const subtitle = tab === "quotes" ? item.project_type : item.email;
              return (
                <div key={item.id} className="border border-line bg-surface" data-testid={`admin-item-${item.id}`}>
                  <button
                    onClick={() => setOpenId(open ? null : item.id)}
                    className="flex w-full items-center gap-4 px-5 py-4 text-left"
                    data-testid={`admin-item-toggle-${item.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-display text-lg font-bold uppercase tracking-wide text-cream">{title}</p>
                      <p className="mt-0.5 truncate font-mono text-[11px] uppercase tracking-widest text-fog">
                        {subtitle} · {fmtDate(item.created_at)}
                      </p>
                    </div>
                    {tab === "quotes" && item.files?.length > 0 && (
                      <span className="shrink-0 border border-brand/40 bg-brand/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-brand">
                        {item.files.length} fichier{item.files.length > 1 ? "s" : ""}
                      </span>
                    )}
                    <ChevronDown className={`h-5 w-5 shrink-0 text-ash transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="border-t border-line px-5 py-4">
                      {tab === "quotes" ? (
                        <>
                          <Row label="Nom" value={item.name} />
                          <Row label="Entreprise" value={item.company} />
                          <Row label="Téléphone" value={item.phone} href={`tel:${item.phone}`} />
                          <Row label="WhatsApp" value={item.whatsapp} />
                          <Row label="Email" value={item.email} href={`mailto:${item.email}`} />
                          <Row label="Localisation" value={item.city} />
                          <Row label="Type de projet" value={item.project_type} />
                          <Row label="Service" value={item.service} />
                          <Row label="Budget" value={item.budget} />
                          <Row label="Délai" value={item.deadline} />
                          <Row label="Description" value={item.description} />
                          {item.files?.length > 0 && (
                            <div className="pt-3">
                              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ash">Fichiers joints</p>
                              <div className="mt-2 flex flex-wrap gap-2">
                                {item.files.map((f, i) => (
                                  <a
                                    key={i}
                                    href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.path}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`admin-file-${item.id}-${i}`}
                                    className="border border-brand/40 bg-brand/10 px-3 py-1.5 font-mono text-[11px] text-brand transition-colors hover:bg-brand hover:text-ink"
                                  >
                                    {f.filename}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <Row label="Nom" value={item.name} />
                          <Row label="Email" value={item.email} href={`mailto:${item.email}`} />
                          <Row label="Téléphone" value={item.phone} />
                          <Row label="Sujet" value={item.subject} />
                          <Row label="Message" value={item.message} />
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
